import { takeUntil, flatMap, tap, filter, map, debounceTime, distinctUntilChanged, delay } from 'rxjs/operators';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Subject, BehaviorSubject } from "rxjs";
import { StreamService } from "../services";
import * as _ from 'lodash';
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { FormControl } from '@angular/forms';

interface SearchParams {
    page: number; 
    rows: number;
    sort?: string,
    sortDirection?: string, 
    itemCountsInContext: boolean;
};

@Component({
    styles: [`
        .mat-header-cell.mat-column-actions {
            width: 80px;
        } 

        .mat-column-type {
            width: 70px;
        }

        .mat-column-counts {
            width: 140px; 
            text-align: center;
        }

        .mat-column-actions {
            width: 40px; 
        }

        .scroll-container {
            max-height: calc(100vh - 329px);
            overflow-y: auto;
        }

        tr.details-row {
            cursor: pointer;
        }

        tr.preview-row {
            height: 0;
        }
    `],
    animations: [
        trigger('detailExpand', [
          state('collapsed', style({height: '0px', minHeight: '0', display: 'none'})),
          state('expanded', style({height: '*'})),
          transition('expanded <=> collapsed', animate('500ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    template: `
        <div fxLayout="column" style="height: calc(100vh - 289px);">
            <mat-toolbar>
                <span fxFlex></span>
                <mat-form-field style="padding-right: 50px; font-size: 16px; font-weight: 400;" class="app-input-white-underline">
                    <input matInput [formControl]="searchBox" placeholder="Search Sources">
                </mat-form-field>
            </mat-toolbar>
            <div *ngIf="noTouching" fxLayout="column" fxLayoutAlign="center center" style="height:100%">
                <spinner syle="padding-top: 100px;"></spinner>
            </div>
            <div *ngIf="!noTouching" 
                class="scroll-container" 
                fxFlex 
                fxLayout="column" 
                infiniteScroll 
                [infiniteScrollThrottle]="300" 
                [scrollWindow]="false" 
                (scrolled)="nextPage()">
            <table mat-table 
                   multiTemplateDataRows
                   *ngIf="!noTouching" 
                   #table 
                   [dataSource]="sources" 
                   matSort 
                   [matSortActive]="params.value.sort" 
                   [matSortDirection]="params.value.sortDirection"
                   [matSortDisableClear]="true"
                   (matSortChange)="onSort($event)" 
                   style="width: 100%;">
                <ng-container matColumnDef="sources">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="title">Sources</th>
                    <td mat-cell *matCellDef="let row" style="overflow: hidden; white-space: nowrap;">
                        <div style="text-overflow: ellipsis; overflow: hidden;" [matTooltip]="row.title">{{row.title}}</div>
                    </td>
                </ng-container>
                <ng-container matColumnDef="type">
                        <th mat-header-cell *matHeaderCellDef mat-sort-header="type">Type</th>
                        <td mat-cell *matCellDef="let row" >
                            <mat-icon [fontSet]="types[row.type].set" [fontIcon]="types[row.type]?.icon" [matTooltip]="types[row.type]?.label"></mat-icon>    
                        </td>
                    </ng-container>
                <ng-container matColumnDef="counts">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="itemsCount">Items Contributed</th>
                    <td mat-cell *matCellDef="let row">{{row.itemsCount}}</td>
                </ng-container>
                <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef>
                        <a mat-icon-button [routerLink]="['../', 'addSources']" style="float: left;">
                            <i class="material-icons">add</i> Sources
                        </a>
                    </th>
                    <td mat-cell *matCellDef="let row">
                        <button mat-icon-button (click)="removeSource(row, $event)" style="float: right;">
                            <i class="material-icons">clear</i>
                        </button>
                    </td>
                </ng-container>
                <ng-container matColumnDef="expandedDetail">
                    <td mat-cell *matCellDef="let row" [attr.colspan]="isSource ? 2 : 3">
                        <div class="element-detail" [@detailExpand]="row == expandedElement ? 'expanded' : 'collapsed'">
                            <edit-topic-content-source-preview *ngIf="expandedElement === row"
                                                               [topic]="topic"
                                                               [source]="row">
                            </edit-topic-content-source-preview>
                        </div>
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="['sources', 'type', 'counts', 'actions']"></tr>
                <tr mat-row 
                    class="details-row"
                    (click)="expandedElement = expandedElement === row ? null : row"
                    *matRowDef="let row; columns: ['sources', 'type', 'counts', 'actions'];"></tr>
                <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="preview-row"></tr>
            </table>
            <div *ngIf="noTouching" fxLayoutAlign="center center" style="height:100%">
                <spinner syle="padding-top: 100px;"></spinner>
            </div>
        </div>
    `
})
export class EditTopicContentSourcesTabComponent implements OnDestroy {
    private cleanupTime = new Subject<void>();
    expandedElement: any;
    isSource: any;
    sources = new BehaviorSubject<any[]>([]);
    params = new BehaviorSubject<SearchParams>({ 
        page: 0, 
        rows: 50,
        itemCountsInContext: true
    });
    types = StreamService.types;
    lastFetched: boolean;
    isPhone: boolean;
    countMap: any;
    noTouching: boolean;
    partialNoTouching: boolean;
    searchBox = new FormControl();
    topic: any;

    constructor(breakpointObserver: BreakpointObserver, 
                route: ActivatedRoute, 
                private streamService: StreamService) {
        breakpointObserver.observe([Breakpoints.Handset]).pipe(takeUntil(this.cleanupTime)).subscribe(result => this.isPhone = result.matches);

        route.parent.parent.params.pipe(takeUntil(this.cleanupTime))
        .pipe(flatMap(params => this.streamService.getStream(params.topicId)))
        .subscribe(topic => {
            this.noTouching = true;
            this.lastFetched = false;
            this.topic = topic;
            this.sources.next([]);
            this.params.next(Object.assign({}, this.params.value, { page: 0 }));
        });

        this.searchBox.valueChanges.pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.cleanupTime))
        .subscribe(term => {
            this.sources.next([]);
            this.params.next(Object.assign({}, this.params.value, { term, page: 0 }));
        });
        this.params.pipe(
            filter(() => !!this.topic),
            flatMap(params => this.streamService.getSubstreams(this.topic.id, params)),
            tap(() => this.noTouching = false),
            tap(() => this.partialNoTouching = false),
            takeUntil(this.cleanupTime)
        ).subscribe(results => this.sources.next(this.sources.value.concat(results.content)));
    }

    removeSource(stream, event): void {
        event.stopPropagation();
        this.streamService.deleteStreamSubscription(this.topic.id, stream.id)
        .pipe(
            tap(() => this.noTouching = true),
            map(() => this.sources.value),
            map(sources => sources.splice(sources.findIndex(str => str.id === stream.id), 1)),
            tap(() => this.sources.next(this.sources.value)),
            tap(() => this.noTouching = false),
            flatMap(() => this.streamService.getStream(this.topic.id, true))
        )
        .subscribe(stream => this.streamService.streamUpdated(stream));
    }

    nextPage(): void {
        this.partialNoTouching = true;
        this.params.next(Object.assign({}, this.params.value, { page: ++this.params.value.page }));
    }

    onSort($event) {
        this.sources.next([]);
        this.params.next(Object.assign({}, this.params.value, { page: 0, sort: $event.active, sortDirection: $event.direction}));
    }

    ngOnDestroy(): void {
        this.cleanupTime.complete();
        this.cleanupTime.next();
    }
}
